import { Injectable, Injector } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpErrorResponse,
  HttpClient,
  HttpContextToken,
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import packageJson from '../../../../package.json';
import { getDeviceInfo } from 'src/utils/browser-info.utils';

export const SKIP_ERROR_LOGGING = new HttpContextToken<boolean>(() => false);

const SEARCH_PORTS_API_ENDPOINT = '/api/v1/autocomplete/port';
const IGNORE_BLENDS_LOGGING_REGEX = /\/api\/v1\/blends\/[^/?]+(?:\?|$)/i;
const IGNORE_SHIP_UUID_LOGGING_REGEX = /\/api\/v1\/ship_uuids\/[^/?]+(?:\?|$)/i;
// Invalid or stale voyage filters are handled as empty results by the Webform.
const IGNORE_VOYAGE_REPORTS_LOGGING_REGEX = /\/api\/v1\/voyages\/[^/?]+\/reports(?:\?|$)/i;
// 400 on this endpoint is an expected validation response; skip logging to avoid noise. (create or update report)
const IGNORE_REPORTS_LOGGING_REGEX =
  /\/api\/v1\/reports\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}(?:\/\d+)?(?:\?|$)/i;
@Injectable()
export class HttpErrorInterceptor implements HttpInterceptor {
  constructor(private injector: Injector) {}

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (req.url.includes('/api/v1/errors')) {
      return next.handle(req);
    }

    return next.handle(req).pipe(
      catchError((error: HttpErrorResponse) => {
        const http = this.injector.get(HttpClient);
        const shipUuid = localStorage.getItem('lastShipUuid');
        const headers: Record<string, string> = {};
        req.headers.keys().forEach((key) => {
          if (!['authorization', 'cookie', 'x-api-key'].includes(key.toLowerCase())) {
            headers[key] = req.headers.get(key) || '';
          }
        });

        const params: Record<string, unknown> = {};
        req.params.keys().forEach((key) => {
          params[key] = req.params.get(key);
        });

        const isSkipErrorLogging = req.context.get(SKIP_ERROR_LOGGING);

        if (isSkipErrorLogging) {
          return throwError(() => error);
        }

        if (error.status === 404 && req.url.includes(SEARCH_PORTS_API_ENDPOINT)) {
          return throwError(() => error);
        }

        if ([400, 404].includes(error.status) && IGNORE_VOYAGE_REPORTS_LOGGING_REGEX.test(req.url)) {
          return throwError(() => error);
        }

        if (error.status === 400 && IGNORE_REPORTS_LOGGING_REGEX.test(req.url)) {
          return throwError(() => error);
        }

        if (error.status === 409 && IGNORE_REPORTS_LOGGING_REGEX.test(req.url) &&
            error.error?.errorCode === 'report_time_conflict') {
          return throwError(() => error);
        }

        if (
          error.status === 0 &&
          (req.method === 'POST' || req.method === 'PUT') &&
          IGNORE_REPORTS_LOGGING_REGEX.test(req.url)
        ) {
          return throwError(() => error);
        }

        if (
          error.status === 0 &&
          req.method === 'GET' &&
          (req.url.includes(SEARCH_PORTS_API_ENDPOINT) ||
            IGNORE_BLENDS_LOGGING_REGEX.test(req.url) ||
            IGNORE_SHIP_UUID_LOGGING_REGEX.test(req.url) ||
            IGNORE_VOYAGE_REPORTS_LOGGING_REGEX.test(req.url))
        ) {
          return throwError(() => error);
        }

        const httpErrorResponse = error?.error ?? {};
        const errorData = {
          url: window.location.href,
          userAgent: navigator.userAgent,
          version: packageJson.version,
          source: 'webform',
          type: 'HTTP Error',
          status: error.status,
          message: `${httpErrorResponse.error ?? 'Unknown error'}, error detail: ${httpErrorResponse.errorDetail ?? 'No details'}`,
          payload: JSON.stringify(req.body),
          details: JSON.stringify({
            endpoint: error.url,
            method: req.method,
            headers: headers,
            params: params,
            message: error.message,
            statusText: error.statusText,
            ...getDeviceInfo(),
          }),
        };
        const url = `${environment.siglarApi.url}/api/v1/errors/${shipUuid}`;
        http.post(url, errorData).subscribe({
          error: (err) => console.error('Logging failed:', err),
        });

        return throwError(() => error);
      }),
    );
  }
}
